import type { IAttachment, IEntityLogEntry, IRisk } from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const base = (projectId: string) => `/api/v1/projects/${projectId}/risks`;

export interface CreateRiskInput {
  title: string;
  impact: string;
  probability: string;
  ownerId: string;
  description?: string | null;
  managementStrategy?: string | null;
  /** `null` explicitly clears the target resolution date on an existing risk; `undefined` leaves it unset at creation. */
  targetResolutionDate?: string | null;
  linkedIssueIds?: string[];
  linkedDependencyIds?: string[];
}
export type UpdateRiskInput = Partial<CreateRiskInput>;

export async function fetchRisks(projectId: string): Promise<IRisk[]> {
  const res = await apiFetch(base(projectId));
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch risks'));
  return res.json() as Promise<IRisk[]>;
}

export async function createRisk(projectId: string, data: CreateRiskInput): Promise<IRisk> {
  const res = await apiFetch(base(projectId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create risk'));
  return res.json() as Promise<IRisk>;
}

export async function updateRisk(projectId: string, riskId: string, data: UpdateRiskInput): Promise<IRisk> {
  const res = await apiFetch(`${base(projectId)}/${riskId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update risk'));
  return res.json() as Promise<IRisk>;
}

export async function transitionRisk(projectId: string, riskId: string, toStep: string): Promise<IRisk> {
  const res = await apiFetch(`${base(projectId)}/${riskId}/transition`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ toStep }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to change risk status'));
  return res.json() as Promise<IRisk>;
}

export async function setRiskActive(projectId: string, riskId: string, active: boolean): Promise<IRisk> {
  const res = await apiFetch(`${base(projectId)}/${riskId}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update risk status'));
  return res.json() as Promise<IRisk>;
}

export async function fetchRiskLog(projectId: string, riskId: string): Promise<IEntityLogEntry[]> {
  const res = await apiFetch(`${base(projectId)}/${riskId}/log`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch risk log'));
  return res.json() as Promise<IEntityLogEntry[]>;
}

export async function addRiskLogEntry(projectId: string, riskId: string, note: string): Promise<IEntityLogEntry[]> {
  const res = await apiFetch(`${base(projectId)}/${riskId}/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to add log entry'));
  return res.json() as Promise<IEntityLogEntry[]>;
}

export async function fetchRiskAttachments(projectId: string, riskId: string): Promise<IAttachment[]> {
  const res = await apiFetch(`${base(projectId)}/${riskId}/attachments`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch attachments'));
  return res.json() as Promise<IAttachment[]>;
}

export async function uploadRiskAttachment(projectId: string, riskId: string, file: File): Promise<IAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiFetch(`${base(projectId)}/${riskId}/attachments`, { method: 'POST', body: formData });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to upload attachment'));
  return res.json() as Promise<IAttachment>;
}

export async function removeRiskAttachment(projectId: string, riskId: string, attachmentId: string): Promise<void> {
  const res = await apiFetch(`${base(projectId)}/${riskId}/attachments/${attachmentId}/deactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to remove attachment'));
}

export function riskAttachmentDownloadUrl(projectId: string, riskId: string, attachmentId: string): string {
  return `${base(projectId)}/${riskId}/attachments/${attachmentId}/download`;
}
